import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import FeedbackList from "@/components/feedback/FeedbackList";
import FeedbackStatusFilter from "@/components/feedback/FeedbackStatusFilter";
import FeedbackSearch from "@/components/feedback/FeedbackSearch";
import BoardSelector from "@/components/feedback/BoardSelector";
import { useToast } from "@/hooks/use-toast";

type FeedbackManagementProps = {
  feedback: any[];
  onFeedbackUpdate: () => Promise<void>;
  companyStatuses?: any[];
  companyId?: string;
};

const FeedbackManagement = ({
  feedback,
  onFeedbackUpdate,
  companyStatuses = [],
  companyId,
}: FeedbackManagementProps) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<"newest" | "popular">("newest");

  useEffect(() => {
    console.log("FeedbackManagement - Company ID:", companyId);
    console.log("FeedbackManagement - Feedback count:", feedback.length);
  }, [companyId, feedback]);

  useEffect(() => {
    setSelectedStatuses([]);
  }, [companyStatuses]);

  const filteredFeedback = feedback.filter((item) => {
    if (
      searchQuery &&
      !item.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !item.description.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    if (
      selectedStatuses.length > 0 &&
      !selectedStatuses.includes(item.status)
    ) {
      return false;
    }

    if (selectedBoardId && item.board_id !== selectedBoardId) {
      return false;
    }

    return true;
  });

  const sortedFeedback = [...filteredFeedback].sort((a, b) => {
    if (sortOption === "newest") {
      return (
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime()
      );
    } else {
      return b.votes - a.votes;
    }
  });

  const handleVote = async (id: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to vote",
          variant: "destructive",
        });
        return;
      }

      const { data: existingVote } = await supabase
        .from("feedback_votes")
        .select("*")
        .eq("feedback_id", id)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existingVote) {
        await supabase
          .from("feedback_votes")
          .delete()
          .eq("id", existingVote.id);
      } else {
        await supabase.from("feedback_votes").insert({
          feedback_id: id,
          user_id: user.id,
        });
      }

      await onFeedbackUpdate();
    } catch (error) {
      console.error("Error handling vote:", error);
      toast({
        title: "Error",
        description: "Failed to update vote",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from("feedback")
        .update({ status })
        .eq("id", id);

      if (error) {
        throw error;
      }

      await onFeedbackUpdate();
      toast({
        title: "Success",
        description: "Feedback status updated",
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleDeleteFeedback = async (id: string) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this feedback?"
      );
      if (!confirmed) return;

      const { error } = await supabase.from("feedback").delete().eq("id", id);

      if (error) {
        throw error;
      }

      await onFeedbackUpdate();
      toast({
        title: "Success",
        description: "Feedback deleted",
      });
    } catch (error) {
      console.error("Error deleting feedback:", error);
      toast({
        title: "Error",
        description: "Failed to delete feedback",
        variant: "destructive",
      });
    }
  };

  const handleSaveScores = async (
    id: string,
    data: {
      score_method: "value_effort" | "rice";
      value_score?: number;
      effort_score?: number;
      reach_score?: number;
      impact_score?: number;
      confidence_score?: number;
      effort_rice_score?: number;
    }
  ) => {
    try {
      let priorityScore = 0;
      if (
        data.score_method === "value_effort" &&
        data.value_score &&
        data.effort_score
      ) {
        priorityScore = data.value_score / (data.effort_score || 1);
      } else if (
        data.reach_score &&
        data.impact_score &&
        data.confidence_score &&
        data.effort_rice_score
      ) {
        priorityScore =
          (data.reach_score * data.impact_score * data.confidence_score) /
          (data.effort_rice_score || 1);
      }

      const updateData = {
        ...data,
        priority_score: priorityScore,
      };

      const { error } = await supabase
        .from("feedback")
        .update(updateData)
        .eq("id", id);

      if (error) {
        throw error;
      }

      await onFeedbackUpdate();
      toast({
        title: "Success",
        description: "Feedback scores updated",
      });
    } catch (error) {
      console.error("Error updating scores:", error);
      toast({
        title: "Error",
        description: "Failed to update scores",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <BoardSelector
            companyId={companyId}
            selectedBoardId={selectedBoardId}
            onBoardSelect={setSelectedBoardId}
          />
        </div>

        <div className="lg:col-span-3 space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div>
              <FeedbackStatusFilter
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                companyStatuses={companyStatuses}
              />
            </div>
            <div className="w-full sm:w-80">
              <FeedbackSearch
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortOption={sortOption}
                setSortOption={setSortOption}
              />
            </div>
          </div>

          <FeedbackList
            feedback={sortedFeedback}
            companyStatuses={companyStatuses}
            onVote={handleVote}
            isAdmin={true}
            onStatusChange={handleStatusChange}
            onDelete={handleDeleteFeedback}
            onSaveScores={handleSaveScores}
          />

          {sortedFeedback.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No feedback found matching your filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagement;
